import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Button,
  Group,
  NumberInput,
  Select,
  Stack,
  Tabs,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconDeviceFloppy, IconPlus, IconX } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/api/admin';
import { errorMessage } from '@/api/http';
import { qk } from '@/api/queries';
import {
  LAYOUT_BANDS,
  SLOT_KINDS,
  type HomeLayout,
  type HomeSlot,
  type LayoutBand,
  type SlotKind,
} from '@/api/types';
import { useCan } from '@/auth/useCan';
import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { CollectionSelect } from '@/components/pickers';
import { QueryState } from '@/components/QueryState';
import { SortableList } from '@/components/SortableList';
import { BAND_LABEL, SLOT_LABEL } from '@/lib/layout';

type EditableSlot = HomeSlot & { key: string };
let keySeq = 0;
const withKeys = (slots: HomeSlot[]): EditableSlot[] =>
  slots.map((s) => ({ ...s, key: `slot-${++keySeq}` }));

export function HomeLayoutsPage() {
  const canEdit = useCan('editHomeLayouts');
  const query = useQuery({
    queryKey: qk.homeLayouts,
    queryFn: () => adminApi.listHomeLayouts(),
    enabled: canEdit,
  });
  const [band, setBand] = useState<LayoutBand>('default');
  const header = (
    <PageHeader
      title="Home layouts"
      description="The rows on each child’s homepage, in order. Each age band can have its own layout; bands without one use the default."
    />
  );
  // The API only lets approvers and admins read or change layouts.
  if (!canEdit)
    return (
      <>
        {header}
        <EmptyState title="Approvers only">
          Home layouts decide what every child sees first, so only approvers and admins can view and
          change them. Add programmes to collections and ask an approver to place them.
        </EmptyState>
      </>
    );
  return (
    <>
      {header}
      <QueryState query={query}>
        {(page) => (
          <Tabs
            value={band}
            onChange={(v) => setBand((v as LayoutBand) ?? 'default')}
            keepMounted={false}
          >
            <Tabs.List mb="md">
              {LAYOUT_BANDS.map((b) => (
                <Tabs.Tab key={b} value={b}>
                  {BAND_LABEL[b]}
                </Tabs.Tab>
              ))}
            </Tabs.List>
            {LAYOUT_BANDS.map((b) => (
              <Tabs.Panel key={b} value={b}>
                <BandEditor
                  band={b}
                  layout={page.find((l) => l.band === b) ?? { band: b, slots: [] }}
                />
              </Tabs.Panel>
            ))}
          </Tabs>
        )}
      </QueryState>
    </>
  );
}

function BandEditor({ band, layout }: { band: LayoutBand; layout: HomeLayout }) {
  const qc = useQueryClient();
  const canEdit = useCan('editHomeLayouts');
  const [slots, setSlots] = useState<EditableSlot[]>(() => withKeys(layout.slots));
  const serverJson = JSON.stringify(layout.slots.map(strip));
  const dirty = JSON.stringify(slots.map(strip)) !== serverJson;

  useEffect(() => {
    setSlots(withKeys(layout.slots));
  }, [serverJson, layout.slots]);

  const save = useMutation({
    mutationFn: () => adminApi.putHomeLayout(band, slots.map(strip)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.homeLayouts });
      notifications.show({
        color: 'leaf',
        message: `${BAND_LABEL[band]} layout saved. The app picks it up straight away.`,
      });
    },
    onError: (e) =>
      notifications.show({ color: 'coral', title: 'Not saved', message: errorMessage(e) }),
  });

  const invalid =
    slots.some((s) => s.slot_kind === 'collection' && !s.collection_id) ||
    slots.some((s) => s.max_items < 1 || s.max_items > 24);
  const update = (key: string, p: Partial<HomeSlot>) =>
    setSlots((list) => list.map((s) => (s.key === key ? { ...s, ...p } : s)));

  return (
    <Stack gap="sm">
      {band !== 'default' && slots.length === 0 && (
        <Alert color="gray" variant="light">
          No layout for {BAND_LABEL[band]} — children in this band get the default layout. Add rows
          to give them their own.
        </Alert>
      )}
      {slots.length === 0 ? (
        band === 'default' && (
          <EmptyState title="No rows">Add rows to build the default homepage.</EmptyState>
        )
      ) : (
        <SortableList
          label={`${BAND_LABEL[band]} rows`}
          items={slots}
          getId={(s) => s.key}
          getLabel={(s) => SLOT_LABEL[s.slot_kind].label}
          onChange={setSlots}
          disabled={!canEdit}
          renderItem={(s) => (
            <Group gap="sm" wrap="wrap" align="flex-end">
              <Select
                label="Row"
                size="xs"
                w={210}
                allowDeselect={false}
                disabled={!canEdit}
                data={SLOT_KINDS.map((k) => ({ value: k, label: SLOT_LABEL[k].label }))}
                value={s.slot_kind}
                onChange={(v) =>
                  update(s.key, {
                    slot_kind: v as SlotKind,
                    collection_id: v === 'collection' || v === 'hero' ? s.collection_id : null,
                  })
                }
              />
              {(s.slot_kind === 'collection' || s.slot_kind === 'hero') && (
                <CollectionSelect
                  label="Collection"
                  size="xs"
                  w={220}
                  clearable={s.slot_kind === 'hero'}
                  disabled={!canEdit}
                  nothingFound="No matching collections"
                  value={s.collection_id ?? null}
                  valueLabel={s.collection_title}
                  error={
                    s.slot_kind === 'collection' && !s.collection_id
                      ? 'Choose a collection'
                      : undefined
                  }
                  onChange={(v, c) =>
                    update(s.key, { collection_id: v, collection_title: c?.title ?? null })
                  }
                />
              )}
              <NumberInput
                label="Max items"
                size="xs"
                w={96}
                min={1}
                max={24}
                disabled={!canEdit}
                value={s.max_items}
                onChange={(v) => update(s.key, { max_items: Number(v) || 1 })}
              />
              <Text size="xs" c="dimmed" style={{ flex: 1, minWidth: 140 }}>
                {SLOT_LABEL[s.slot_kind].help}
              </Text>
              {canEdit && (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  aria-label={`Remove ${SLOT_LABEL[s.slot_kind].label} row`}
                  onClick={() => setSlots((list) => list.filter((x) => x.key !== s.key))}
                >
                  <IconX size={14} />
                </ActionIcon>
              )}
            </Group>
          )}
        />
      )}
      {canEdit && (
        <Group justify="space-between">
          <Button
            variant="default"
            leftSection={<IconPlus size={16} />}
            onClick={() =>
              setSlots((list) => [
                ...list,
                ...withKeys([{ slot_kind: 'collection', collection_id: null, max_items: 12 }]),
              ])
            }
          >
            Add row
          </Button>
          <Group gap="xs">
            {dirty && (
              <Button
                variant="subtle"
                color="gray"
                onClick={() => setSlots(withKeys(layout.slots))}
              >
                Discard
              </Button>
            )}
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              disabled={!dirty || invalid}
              loading={save.isPending}
              onClick={() => save.mutate()}
            >
              Save layout
            </Button>
          </Group>
        </Group>
      )}
    </Stack>
  );
}

function strip(s: HomeSlot): Omit<HomeSlot, 'collection_title'> {
  return { slot_kind: s.slot_kind, collection_id: s.collection_id ?? null, max_items: s.max_items };
}
